/** `"true"`/`"1"`/`"yes"` → true, `"false"`/`"0"`/`"no"` → false, unset → fallback. */
const flag = (value: string | undefined, fallback: boolean): boolean => {
    if (value === undefined || value === "") return fallback;
    return ["true", "1", "yes", "on"].includes(value.trim().toLowerCase());
};

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

const config = {
    API_URL: process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL,
    SOCKET_URL: process.env.REACT_APP_SOCKET_URL,

    // Google Analytics (GA4) — see ANALYTICS.md.
    //
    // Analytics is opt-in twice over: an id must be present AND the kill switch must
    // not be off. Two knobs rather than one because they answer different questions:
    // "is a property provisioned for this tier?" (id) and "do we want to send to it
    // right now?" (flag). The flag lets an env be muted without ripping the id out of
    // terraform, and lets a developer with a real id in their shell run with GA off.
    GA_MEASUREMENT_ID,
    ANALYTICS_ENABLED: flag(process.env.REACT_APP_ANALYTICS_ENABLED, Boolean(GA_MEASUREMENT_ID)),
    // Sends `debug_mode` so hits appear in GA4 DebugView, and mirrors every event to
    // `window.__conciergeAnalytics` for inspection. Never enable on production.
    ANALYTICS_DEBUG: flag(process.env.REACT_APP_ANALYTICS_DEBUG, false),

    // Reported to GA as the `env` dimension. No NODE_ENV fallback on purpose: CRA sets
    // NODE_ENV=production for every `yarn build`, so falling back to it labelled d1,
    // q2, s3 and p5 alike as "production" and made the tiers indistinguishable inside
    // a shared data stream (ANALYTICS.md B2). "unknown" is a visible defect in a
    // report; "production" is an invisible one.
    // Browser RUM (Grafana Faro → /faro/collect → Alloy). Separate switch from
    // GA on purpose: they answer different questions and one is often wanted
    // without the other. Off unless explicitly enabled, because it needs an Alloy
    // receiver behind the portal's own origin to send to.
    RUM_ENABLED: flag(process.env.REACT_APP_RUM_ENABLED, false),
    // Fraction of SESSIONS to record, 0..1. Whole sessions, never individual
    // events: a half-sampled session is half a trace, which is worse to read
    // than none. 1 on d1 and q2 (the tiers we debug on); lower where the
    // traffic is real. Set per tier by the pipeline, not baked in here.
    RUM_SAMPLING_RATE: (() => {
        const raw = Number(process.env.REACT_APP_RUM_SAMPLING_RATE);
        return Number.isFinite(raw) && raw >= 0 && raw <= 1 ? raw : 1;
    })(),
    APP_VERSION: process.env.REACT_APP_VERSION || "",

    ENV: process.env.REACT_APP_ENVIRONMENT || "unknown"
};

export default config;
